import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { is401Error } from "@/shared/api/errors"
import { queryKeyBuilder } from "@/shared/lib/query"
import { fetchSession, login, logout } from "./api"
import type { Session } from "./types"

export const sessionQueryKey = () => queryKeyBuilder("session")

/**
 * Restores the session from the cookie. A 401 here is an answer and not a failure, so it is
 * neither retried nor turned into a redirect; the pages decide where a visitor belongs.
 *
 * A 401 also leaves the query without data, and such a query is reset to pending whenever it
 * refetches — on every window focus, among others. A page that shows a loader while the
 * session resolves has to pair `isPending` with `isFetched` to tell the first load from a
 * refetch, or it will tear itself down under a visitor who merely switched tabs.
 */
export function useSessionQuery() {
  return useQuery({
    queryKey: sessionQueryKey(),
    queryFn: fetchSession,
    retry: (failureCount, error) => !is401Error(error) && failureCount < 2,
    staleTime: 5 * 60 * 1000,
  })
}

export function useLoginMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: login,
    onSuccess: (session: Session) => {
      queryClient.setQueryData(sessionQueryKey(), session)
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: logout,
    // Datasets and export tasks belong to the user who just left, so the cache goes with them.
    onSettled: () => {
      queryClient.clear()
    },
  })
}
